import React, { useEffect, useMemo, useState } from 'react';
import './DeviceManagerModal.css';
import apiService from '../services/api';
import { getMiniApp, triggerHaptic } from '../services/telegram';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const statusText = (status) => {
  if (status === 'active') return 'Активно';
  if (status === 'blocked') return 'Заблокировано';
  if (status === 'revoked') return 'Отключено';
  return status || '—';
};

function DeviceManagerModal({ isOpen, onClose, userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [slots, setSlots] = useState(1);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    if (userId == null || userId === '') return;
    setLoading(true);
    setError('');
    try {
      setData(await apiService.getDevices(userId));
    } catch (e) {
      setError(e?.message || 'Не удалось загрузить устройства');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setSlots(1);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  const normalizedSlots = Math.max(1, Math.min(1000, Number(slots) || 1));
  const total = useMemo(
    () => normalizedSlots * Number(data?.price_per_extra_slot || 49),
    [normalizedSlots, data?.price_per_extra_slot],
  );

  if (!isOpen) return null;

  const changeDevice = async (device, action) => {
    setBusyId(device.id);
    setError('');
    try {
      const next = action === 'restore'
        ? await apiService.restoreDevice(userId, device.id)
        : await apiService.revokeDevice(userId, device.id);
      setData((current) => ({ ...current, ...next }));
    } catch (e) {
      setError(e?.message || 'Не удалось изменить устройство');
    } finally {
      setBusyId(null);
    }
  };

  const buySlots = async () => {
    if (paying) return;
    setPaying(true);
    setError('');
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const successBase = (origin && origin.startsWith('https://')
      ? origin
      : (process.env.REACT_APP_SUCCESS_BASE_URL || '')).replace(/\/$/, '');
    try {
      const result = await apiService.deviceSlotsCheckout({
        userid: userId,
        slots: normalizedSlots,
        return_url: `${successBase}/success.html`,
        payment_method: 'card',
      });
      if (!result?.confirmation_url) throw new Error('Не удалось получить ссылку на оплату');
      try {
        sessionStorage.setItem('beznet_pending_payment_id', String(result.payment_id));
      } catch (_) {}
      const miniApp = getMiniApp?.();
      if (miniApp?.openLink) miniApp.openLink(result.confirmation_url);
      else window.location.assign(result.confirmation_url);
    } catch (e) {
      setError(e?.message || 'Не удалось создать платёж');
      setPaying(false);
    }
  };

  const devices = Array.isArray(data?.devices) ? data.devices : [];
  return (
    <div className="devices-overlay" onClick={() => { triggerHaptic(); onClose?.(); }}>
      <div className="devices-sheet" onClick={(event) => event.stopPropagation()}>
        <div className="devices-header">
          <h2>Устройства</h2>
          <button type="button" className="devices-close" onClick={() => onClose?.()} aria-label="Закрыть">×</button>
        </div>
        <div className="devices-body">
          {loading && <div className="devices-message">Загрузка…</div>}
          {!loading && data && (
            <>
              <section className="devices-summary">
                <div><span>Используется</span><strong>{data.used_slots} из {data.device_limit}</strong></div>
                <div><span>Включено в подписку</span><strong>{data.base_limit}</strong></div>
                <div><span>Дополнительных мест</span><strong>{data.extra_slots}</strong></div>
              </section>

              <section className="devices-list" aria-label="Список устройств">
                {devices.length === 0 && (
                  <div className="devices-message">Устройства появятся после обновления подписки в Happ.</div>
                )}
                {devices.map((device) => (
                  <article key={device.id} className={`device-card device-card-${device.status}`}>
                    <div className="device-card-main">
                      <strong>{device.name}</strong>
                      <span className={`device-status device-status-${device.status}`}>{statusText(device.status)}</span>
                    </div>
                    <div className="device-meta">
                      {[device.device_os, device.os_version].filter(Boolean).join(' · ') || 'Happ'}
                      <span>Последняя активность: {formatDate(device.last_seen_at)}</span>
                    </div>
                    {device.status === 'revoked' ? (
                      <button type="button" disabled={busyId === device.id} onClick={() => changeDevice(device, 'restore')}>
                        Подключить снова
                      </button>
                    ) : (
                      <button type="button" disabled={busyId === device.id} onClick={() => changeDevice(device, 'revoke')}>
                        Отключить устройство
                      </button>
                    )}
                  </article>
                ))}
              </section>

              <section className="devices-buy">
                <div className="devices-buy-copy">
                  <strong>Добавить места</strong>
                  <span>49 ₽ за каждое место до окончания текущей подписки</span>
                </div>
                <div className="devices-counter">
                  <button type="button" onClick={() => setSlots(Math.max(1, normalizedSlots - 1))}>−</button>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={slots}
                    onChange={(event) => setSlots(event.target.value)}
                    aria-label="Количество дополнительных мест"
                  />
                  <button type="button" onClick={() => setSlots(normalizedSlots + 1)}>+</button>
                </div>
                <button type="button" className="devices-pay" disabled={paying} onClick={buySlots}>
                  {paying ? 'Открываем оплату…' : `Купить за ${total} ₽`}
                </button>
              </section>
            </>
          )}
          {error && <div className="devices-error">{error}</div>}
        </div>
      </div>
    </div>
  );
}

export default DeviceManagerModal;
